// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import {
  loadBulkIngestedTransactions,
  exportBulkIngestedTransactionsData,
} from '../../../actions/keyCodeActions';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import TransactionStatusBadge from './TransactionStatusBadge';
import useUploadNotificationsGeneric from '../../../hooks/useUploadNotificationsGeneric';
import { hasPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import UserLink from '../../common/links/UserLink';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks';

export interface BulkIngestedBatchesListProps {
  reload?: any;
  bulkTransactions?: any;
  currentUser?: any;
}

function BulkIngestedBatchesList({ reload, bulkTransactions }: BulkIngestedBatchesListProps) {
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const allColumns = [
    'uploadedDate',
    'filename',
    'keyProviderName',
    'keyCount',
    'createdByUser',
    'status',
    'message',
    'importTransactionStatus',
  ];
  const defaultVisibleColumns = allColumns;
  const [transactions, setTransactions] = useState<any>();
  const params = new URLSearchParams(location.search);

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'bulkingestedbatcheslist-settings',
    getTableSettingsDefaults({
      defaultSort: 'uploadedDate',
      direction: 'desc',
      defaultVisibleColumns,
    }),
    null,
    loadBulkIngestedTransactions,
    setTransactions,
    reload,
    t('Failed to load transactions')
  );

  useUploadNotificationsGeneric(
    transactions,
    setTransactions,
    'bulkTransaction',
    bulkTransactions,
    'id',
    (row: any) => row.status === 'Pending'
  );

  let columnsConfig = [
    {
      Key: 'uploadedDate',
      Label: t('Uploaded date'),
      width: '10%',
      Render: (item: any) =>
        item.importTransactionId ? (
          <Link to={'/keys/ingested-batches/' + item.importTransactionId}>
            <FormattedDate value={item.uploadedDate} variant="default" />
          </Link>
        ) : (
          <FormattedDate value={item.uploadedDate} variant="default" />
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'filename',
      Label: t('Filename'),
      width: '20%',
      Render: (item: any) => item.filename,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'status',
      Label: t('Status'),
      width: '10%',
      Render: (item: any) => (
        <TransactionStatusBadge
          success={item.success}
          partialSuccess={item.partialSuccess}
          message={item.message}
          status={item.status}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'importTransactionStatus',
      Label: t('Import Transaction Status'),
      width: '10%',
      Render: (item: any) =>
        item.importTransactionId ? (
          <TransactionStatusBadge
            success={item.importTransactionSuccess}
            status={item.importTransactionStatus}
          />
        ) : (
          ''
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyProviderName',
      Label: t('Key provider'),
      width: '10%',
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Key pool'),
      width: '10%',
      Render: (item: any) =>
        hasPermission(currentUser, permissionTypes.MANAGE_WORKFLOWS) ? (
          <NavLink to={`/settings/workflow/${item.keyWorkflowId}`}>
            {item.keyWorkflowName}
          </NavLink>
        ) : (
          item.keyWorkflowName
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'message',
      Label: t('Message'),
      width: '10%',
      Render: (item: any) => item.message,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyCount',
      Label: t('Key count'),
      width: '5%',
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_NoDecimal(item.keyCount),
    },
    {
      Key: 'createdByUser',
      Label: t('Uploaded by'),
      width: '10%',
      Render: (item: any) => (
        <>
          <UserLink id={item.createdByUserId} name={item.createdByUser} />
          <br />
          {item.createdByUserCompany}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return transactions ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={transactions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={transactions.totalRowCount}
        filteredRowCount={transactions.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportBulkIngestedTransactionsData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default BulkIngestedBatchesList;
